import { getSecondaryCategories } from './categoryTree.js'

/**
 * @description Build a desktop mega menu directly from every visible secondary category under a primary category.
 * @param {Object|null|undefined} primaryCategory - Primary category node from the shared category tree.
 * @param {Object} [options] - Mega-menu mapping options.
 * @param {string} [options.menuKey] - Stable menu cache key.
 * @param {(secondaryCategory: Object) => string|Object|null} [options.resolveAllRoute] - Product-list route resolver.
 * @returns {{ key: string, fromAggregate: false, anchors: Array<Object>, sections: Array<Object> }|null} Category-backed mega menu.
 */
export function buildPrimaryCategoryMegaMenu(primaryCategory, options = {}) {
  const primaryCategoryId = primaryCategory?.categoryId
  const { menuKey = `category-${primaryCategoryId || ''}`, resolveAllRoute = () => null } = options

  if (!primaryCategoryId || !menuKey) return null

  const sections = getSecondaryCategories(primaryCategory).map((secondaryCategory) => ({
    key: `${menuKey}-${secondaryCategory.categoryId}`,
    title: secondaryCategory.categoryName || '',
    categoryId: secondaryCategory.categoryId,
    primaryCategoryId,
    productIds: [],
    hasMore: false,
    loadFromSearch: true,
    allRoute: resolveAllRoute(secondaryCategory),
  }))

  const visibleSections = sections.filter((section) => section.title && section.categoryId)
  if (visibleSections.length === 0) return null

  return {
    key: menuKey,
    fromAggregate: false,
    anchors: visibleSections.map((section) => ({ key: section.key, label: section.title })),
    sections: visibleSections,
  }
}

/**
 * @description Resolve the product cards shown for one mega-menu category section.
 * @param {Array<Object>} records - Product search records.
 * @param {number|string|null|undefined} total - Product search total.
 * @param {number} [visibleCount=6] - Maximum number of occupied card slots.
 * @returns {{ products: Array<Object>, hasMore: boolean }} Visible products and more-card state.
 */
export function buildMegaMenuProductPreview(records, total, visibleCount = 6) {
  const normalizedRecords = Array.isArray(records) ? records : []
  const normalizedVisibleCount = Number.isFinite(Number(visibleCount))
    ? Math.max(Math.trunc(Number(visibleCount)), 1)
    : 6
  const normalizedTotal = Number(total)
  const hasMore = Number.isFinite(normalizedTotal)
    ? normalizedTotal > normalizedVisibleCount
    : normalizedRecords.length > normalizedVisibleCount
  const productLimit = hasMore ? Math.max(normalizedVisibleCount - 1, 0) : normalizedVisibleCount

  return {
    products: normalizedRecords.slice(0, productLimit),
    hasMore,
  }
}
